import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft,
  ArrowLeft,
  Sparkles,
  Check
} from "lucide-react";

const MOODS = [
  { id: "joyful", emoji: "😊", label: "Joyful", color: "from-yellow-400/30 to-amber-400/30" },
  { id: "peaceful", emoji: "😌", label: "Peaceful", color: "from-green-400/30 to-emerald-400/30" },
  { id: "grateful", emoji: "🙏", label: "Grateful", color: "from-pink-400/30 to-rose-400/30" },
  { id: "hopeful", emoji: "✨", label: "Hopeful", color: "from-purple-400/30 to-violet-400/30" },
  { id: "neutral", emoji: "😐", label: "Neutral", color: "from-gray-400/30 to-slate-400/30" },
  { id: "anxious", emoji: "😰", label: "Anxious", color: "from-orange-400/30 to-amber-400/30" },
  { id: "sad", emoji: "😢", label: "Sad", color: "from-blue-400/30 to-indigo-400/30" },
  { id: "frustrated", emoji: "😤", label: "Frustrated", color: "from-red-400/30 to-orange-400/30" },
  { id: "tired", emoji: "😴", label: "Tired", color: "from-indigo-400/30 to-purple-400/30" },
];

export default function MoodTrackerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("select"); // select, notes, complete
  const [saving, setSaving] = useState(false);

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
  };

  const handleContinue = () => {
    if (selectedMood) {
      setStep("notes");
    }
  };

  const handleSave = async () => {
    if (!selectedMood) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/journal`,
        {
          title: `Mood: ${selectedMood.label}`,
          body: notes || `Feeling ${selectedMood.label.toLowerCase()} today.`,
          tags: ["mood", selectedMood.id],
          source: "mood_tracker",
          mood: selectedMood.id
        },
        { 
          withCredentials: true 
        }
      );
      setStep("complete");
      toast.success("Mood tracked successfully!");
    } catch (error) {
      console.error("Failed to save mood:", error);
      toast.error("Failed to save mood. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    navigate("/compass");
  };

  return (
    <div className="min-h-screen bg-[#0b2821] pb-24" data-testid="mood-tracker-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0b2821]/95 backdrop-blur-sm border-b border-[#a69373]/20">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate("/compass")}
            className="p-2 -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#a69373]" />
            </div>
            <h1 className="text-lg font-heading text-[#a69373]">Mood Tracker</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {step === "select" && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-heading text-white mb-1">How are you feeling?</h2>
              <p className="text-white/60 text-sm">Select the mood that best describes you right now</p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {MOODS.map((mood) => (
                <button
                  key={mood.id}
                  onClick={() => handleMoodSelect(mood)}
                  className={`p-2 rounded-lg border-2 transition-all flex items-center gap-2 h-16 ${
                    selectedMood?.id === mood.id
                      ? "border-[#a69373] bg-[#a69373]/10"
                      : "border-white/10 hover:border-white/20"
                  }`}
                  data-testid={`mood-${mood.id}`}
                >
                  <span className="text-2xl">{mood.emoji}</span>
                  <p className="text-white/80 text-xs font-medium text-left">{mood.label}</p>
                </button>
              ))}
            </div>

            <Button
              onClick={handleContinue}
              disabled={!selectedMood}
              className="w-full mt-4 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </div>
        )}

        {step === "notes" && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${selectedMood.color} flex items-center justify-center mb-4`}>
                <span className="text-5xl">{selectedMood.emoji}</span>
              </div>
              <h2 className="text-xl font-heading text-white">Feeling {selectedMood.label}</h2>
            </div>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <label className="text-white/60 text-sm mb-2 block">
                  What's on your mind? (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Share more about how you're feeling..."
                  className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  data-testid="mood-notes"
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("select")}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
                data-testid="save-mood-button"
              >
                {saving ? "Saving..." : "Save Mood"}
              </Button>
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">Mood Tracked!</h2>
            <p className="text-white/60 mb-8">
              Great job checking in with yourself. Regular mood tracking helps you understand your patterns.
            </p>
            <Button
              onClick={handleFinish}
              className="bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
              data-testid="finish-button"
            >
              Return to Home
            </Button>
          </div>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
